import { useMfaSetup } from '../hooks/useMfaSetup'
import {
  MfaSetupBackupCodesStep,
  MfaSetupConfirmStep,
  MfaSetupEmailOtpStep,
  MfaSetupMethodSelectStep,
  MfaSetupQrStep,
} from './MfaSetupSteps'
import { useTranslation } from 'react-i18next'

/** Wymuszona konfiguracja MFA przy pierwszym logowaniu (/auth/mfa-setup) —
 *  prezentacja z `app/prototypes/src/features/auth/components/MfaSetup.tsx`:
 *  method-select → (TOTP: qr → confirm) lub (EMAIL: email-otp) → backup.
 *  Orkiestracja, guard `mfaPending.type === 'SETUP'` i Redux zostają w `useMfaSetup`.
 *
 *  Krok kodów zapasowych renderuje się w tej samej karcie co pozostałe kroki
 *  (mockup), a nie w osobnym modalu — użytkownik i tak nie może go pominąć,
 *  bo jedyne wyjście prowadzi przez „przejdź do aplikacji". */
export const MfaSetup = () => {
  const { t } = useTranslation()
  const {
    step,
    otpauthUrl,
    secret,
    totpCode,
    setTotpCode,
    backupCodes,
    isLoading,
    secretCopied,
    setupError,
    emailOtpCode,
    setEmailOtpCode,
    emailSending,
    emailSent,
    emailError,
    resendSecondsLeft,
    handleTotpEnableSubmit,
    copySecret,
    pickTotp,
    pickEmail,
    resendSetupEmail,
    handleEmailEnableSubmit,
    handleFinish,
    downloadBackupCodes,
    goToConfirmFromQr,
    backToQrFromConfirm,
    backToMethodSelectFromQr,
    backToMethodSelectFromEmailOtp,
  } = useMfaSetup()

  return (
    <section className='w-full max-w-crane-card rounded-crane-md bg-crane-card p-3 md:p-6 shadow-crane-soft'>
      {step === 'method-select' ? (
        <MfaSetupMethodSelectStep
          emailSending={emailSending}
          emailError={emailError}
          onPickEmail={pickEmail}
          onPickTotp={pickTotp}
          hideBackButton
        />
      ) : null}

      {step === 'qr' ? (
        <MfaSetupQrStep
          otpauthUrl={otpauthUrl}
          secret={secret}
          secretCopied={secretCopied}
          onCopySecret={copySecret}
          onNext={goToConfirmFromQr}
          onBack={backToMethodSelectFromQr}
        />
      ) : null}

      {step === 'confirm' ? (
        <MfaSetupConfirmStep
          totpCode={totpCode}
          setTotpCode={setTotpCode}
          isLoading={isLoading}
          onSubmit={handleTotpEnableSubmit}
          onBack={backToQrFromConfirm}
          error={setupError}
        />
      ) : null}

      {step === 'email-otp' ? (
        <MfaSetupEmailOtpStep
          emailOtpCode={emailOtpCode}
          setEmailOtpCode={setEmailOtpCode}
          isLoading={isLoading}
          emailSending={emailSending}
          emailSent={emailSent}
          emailError={emailError}
          resendSecondsLeft={resendSecondsLeft}
          onSubmit={handleEmailEnableSubmit}
          onResend={resendSetupEmail}
          onBack={backToMethodSelectFromEmailOtp}
        />
      ) : null}

      {step === 'backup' ? (
        <MfaSetupBackupCodesStep
          backupCodes={backupCodes}
          continueLabel={t('mfaSetup.backupContinue')}
          onContinue={handleFinish}
          onDownload={downloadBackupCodes}
        />
      ) : null}
    </section>
  )
}
